import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixIdentityOauthV1RefreshToken from './identity-oauth-v1-refresh-token-oauth.http.js';

export interface RefreshToken {
  token?: string;
}

/**
 * TokenRequest is sent by the client to the authorization server to request an
 * access token.
 */
export interface TokenRequest extends TokenRequestTenantOneOf {
  /** supported tenant - specific site */
  siteId?: string | null;
  /** supported tenant - specific account */
  accountId?: string | null;
  /** The application id that want to obtain an access token. */
  clientId?: string | null;
  /**
   * grant_type specifies the grant type being used. supported grant types are:
   * `authorization_code`, `client_credentials`, `refresh_token`, `urn:ietf:params:oauth:grant-type:device_code`, `anonymous`,
   * `urn:ietf:params:oauth:grant-type:token-exchange`
   */
  grantType?: string;
  /** Required for `refresh_token` grant type. */
  refreshToken?: string | null;
  /** Required for `client_credentials` grant type. */
  clientSecret?: string | null;
  /**
   * Required for `urn:ietf:params:oauth:grant-type:device_code` grant type. The device verification code, "device_code" from the
   * device authorization response
   */
  deviceCode?: string | null;
  /** Required for the `authorization_code` grant type. */
  redirectUri?: string | null;
  /** Required for `authorization_code` grant type. */
  code?: string | null;
  /** Optional, used if code challenge and code method were used in `authorization_code` grant type. */
  codeVerifier?: string | null;
  /** Required for `authorized_client` grant type - create tokens for a specific user. */
  userId?: string | null;
  /** Required for `authorized_client` grant type - create tokens for a specific member using api-key. */
  memberId?: string | null;
  /**
   * Required for `client_credentials` grant type with clientId that represents an external app.
   * An access token will be created based on the app installed version of an instance.
   */
  instanceId?: string | null;
  /** Required for `authorized_client` grant type for externalApp and must be bounded with client id, create access token to an app with a specific version. */
  appVersion?: string | null;
  /** Required for `strip_bound_session` grant type - create token without bound session. */
  accessToken?: string | null;
  /**
   * Optional for `authorized_client` grant type - create tokens for a specific user.
   * @format GUID
   */
  userAccountId?: string | null;
  /**
   * Optional for `authorized_client` grant type - bind the issued tokens to a specific session (session-bound wixAT/wixRT).
   * Internal-only: used by the server-to-server switchAccount flow via Oauth2NgInternal.
   * @internal
   * @format GUID
   */
  sessionId?: string | null;
  /**
   * Optional for `authorized_client` grant type (member tokens) - a member session token that is opened server-side to
   * extract the bound session id, producing session-bound wixAT/wixRT. Public: used by the member flow from public traffic.
   */
  sessionToken?: string | null;
  /**
   * Optional for `authorized_client` grant type - attach an impersonating user id to the minted token.
   * Internal-only: used by server-to-server flows where a service mints a token carrying impersonation context.
   * @internal
   * @format GUID
   */
  impersonatingUserId?: string | null;
  /**
   * Required for the `urn:ietf:params:oauth:grant-type:token-exchange` grant - the account-level access token being
   * exchanged for one scoped to the target named by the `tenant` oneof.
   */
  subjectToken?: string | null;
  /**
   * Required for the `urn:ietf:params:oauth:grant-type:token-exchange` grant - identifies the type of `subject_token`.
   * Only `urn:ietf:params:oauth:token-type:access_token` is supported.
   * @maxLength 100
   */
  subjectTokenType?: string | null;
}

/** @oneof */
export interface TokenRequestTenantOneOf {
  /** supported tenant - specific site */
  siteId?: string | null;
  /** supported tenant - specific account */
  accountId?: string | null;
}

export interface InternalTokenResponse
  extends InternalTokenResponseResultOneOf {
  success?: TokenResponse;
  failure?: TokenErrorResponse;
}

/** @oneof */
export interface InternalTokenResponseResultOneOf {
  success?: TokenResponse;
  failure?: TokenErrorResponse;
}

export interface TokenResponse {
  /** The access token issued by the authorization server. */
  accessToken?: string;
  /** The type of the token issued as described in Section 7.1. of the oauth2 rfc. */
  tokenType?: string;
  /**
   * The lifetime in seconds of the access token.  For example, the value "3600" denotes that the access token will
   * expire in one hour from the time the response was generated.
   */
  expiresIn?: number;
  /** The refresh token, which can be used to obtain new access tokens. */
  refreshToken?: string | null;
  /** OPTIONAL, if identical to the scope requested by the client; otherwise, will be sent. */
  scope?: string | null;
  idToken?: string | null;
  /**
   * Set only for the `urn:ietf:params:oauth:grant-type:token-exchange` grant, where it is always
   * `urn:ietf:params:oauth:token-type:access_token`. Empty for every other grant.
   * @maxLength 100
   */
  issuedTokenType?: string | null;
}

export interface TokenErrorResponse {
  error?: string;
  errorDescription?: string | null;
}

export interface TokenInfoRequest {
  /** The access token to introspect. */
  token?: string;
}

export interface TokenInfoResponse {
  active?: boolean;
  /** subject type. */
  subjectType?: SubjectTypeWithLiterals;
  /** subject id */
  subjectId?: string;
  /** Expiration time of the token */
  exp?: string | null;
  /** Issued time of the token */
  iat?: string | null;
  /** Client id */
  clientId?: string;
  /** Account id */
  accountId?: string | null;
  /** Site id */
  siteId?: string | null;
  /** Instance Id */
  instanceId?: string | null;
  /**
   * Vendor Product Id
   * @maxLength 100
   */
  vendorProductId?: string | null;
}

export enum SubjectType {
  /** unknown subject type */
  UNKNOWN = 'UNKNOWN',
  /** user subject type */
  USER = 'USER',
  /** visitor subject type */
  VISITOR = 'VISITOR',
  /** member subject type */
  MEMBER = 'MEMBER',
  /** app subject type */
  APP = 'APP',
}

/** @enumType */
export type SubjectTypeWithLiterals =
  | SubjectType
  | 'UNKNOWN'
  | 'USER'
  | 'VISITOR'
  | 'MEMBER'
  | 'APP';

/**
 * AuthorizeRequest is sent by the client to the authorization server to initiate
 * the authorization process.
 */
export interface AuthorizeRequest {
  /**
   * ID of the Wix OAuth app requesting authorization.
   * @format GUID
   */
  clientId?: string;
  /**
   * Desired authorization [grant type](https://auth0.com/docs/authenticate/protocols/oauth#grant-types).
   *
   * Supported values:
   * + `code`: The endpoint returns an authorization code that can be used to obtain an access token.
   * @minLength 1
   */
  responseType?: string;
  /**
   * URI to redirect the browser to after authentication and authorization. The browser is redirected to this URI whether the authentication and authorization process is successful or not.
   * @minLength 1
   */
  redirectUri?: string | null;
  /**
   * Desired scope of access. If this field is left empty, only an access token is granted.
   * To received a refresh token, pass `offline_access` as the value of this field.
   */
  scope?: string | null;
  /**
   * A value used to confirm the state of an application before and after it makes an authorization
   * request. If a value for this field is set in the request, it's added to the `redirectUri` when the browser
   * is redirected there.
   * Learn more about [using the state parameter](https://auth0.com/docs/secure/attack-protection/state-parameters).
   * @minLength 1
   */
  state?: string;
  /**
   * esired response format.
   *
   * Supported values:
   * + `query`: The response parameters are encoded as query string parameters and added to the `redirectUri` when redirecting.
   * + `fragment`: The response parameters are encoded as URI fragment parameters and added to the `redirectUri` when redirecting.
   * + `web_message`: The response parameters are encoded as a JSON object and added to the body of a [web message response](https://datatracker.ietf.org/doc/html/draft-sakimura-oauth-wmrm-00).
   *
   * Default value: `query`
   */
  responseMode?: string | null;
  /**
   * Code challenge to use for PKCE verification.
   * This field is only used if `responseType` is set to `code`.
   */
  codeChallenge?: string | null;
  /**
   * Code challenge method to use for PKCE verification.
   * This field is only used if `responseType` is set to `code`.
   *
   * Supported values:
   * + `S256`: The code challenge is transformed using SHA-256 encyption.
   * + `S512`: The code challenge is transformed using SHA-512 encyption.
   */
  codeChallengeMethod?: string | null;
  /**
   * Token returned by a successful [login](https://dev.wix.com/docs/api-reference/business-management/headless/authentication/login-v-2) or [register](https://dev.wix.com/docs/api-reference/business-management/headless/authentication/register-v-2) call that identifies the authenticated member.
   *
   * In the default SDK flow, session context is passed through a hidden iframe automatically. When using a [full-page redirect](https://dev.wix.com/docs/go-headless/develop-your-project/self-managed-headless/authentication/members/custom-login-page/custom-login/custom-login-using-the-js-sdk#use-a-full-page-redirect-desktop-and-mobile-browsers) instead, you must pass this field explicitly because there is no iframe to carry the session context.
   */
  sessionToken?: string | null;
  /**
   * Optional fields for errors
   * A short error code that describes the type of error that occurred (e.g. "invalid_request")
   * @internal
   */
  error?: string | null;
  /**
   * A human-readable description of the error that occurred
   * @internal
   */
  errorDescription?: string | null;
  /**
   * DEPRECATED: no longer read. The sign-in URL is resolved from the client's OAuthClientConfiguration
   * component (sign_in_url), falling back to the default Wix sign-in page.
   * @internal
   * @format SECURE_WEB_URL
   * @deprecated
   */
  signInUrl?: string | null;
  /**
   * Indicates whether the user has consented to the requested scopes
   * @internal
   */
  userConsented?: boolean | null;
  /**
   * Indicates whether login is optional. If true, will not redirect to the sign_in_url if user is not logged in.
   * @internal
   */
  optionalLogin?: boolean | null;
  /**
   * The site id the authorization is requested for.
   * @internal
   * @format GUID
   */
  metaSiteId?: string | null;
  /**
   * ID of the IAM IdP connection to authenticate with when no member session exists.
   * When set, the sign-in redirect initiates an OIDC login against this IdP connection
   * (IAM's `/sso/login` endpoint) instead of the default Wix login page.
   * @internal
   * @format GUID
   */
  idp?: string | null;
  /**
   * OIDC nonce, bound to the authorization request and echoed back in the id_token.
   * @maxLength 512
   */
  nonce?: string | null;
  /**
   * RFC 9470 / OIDC acr_values: space-separated required ACRs (OR, in preference order) for step-up.
   * @internal
   */
  acrValues?: string | null;
  /**
   * RFC 9470 / OIDC max_age: max seconds since last auth (0 forces re-auth).
   * @internal
   */
  maxAge?: string | null;
  /** OIDC prompt. Only "none" is honored: request silent authorization with no user interaction. */
  prompt?: string | null;
  /**
   * Standard OAuth error code the step-up surface returns on the browser redirect back to authorize
   * (e.g. access_denied, unmet_authentication_requirements). Browser-supplied like acr_values/prompt, so
   * untrusted: allowlisted and forwarded to the client as the OAuth error.
   * @internal
   */
  stepUpError?: string | null;
}

export interface RawHttpResponse {
  body?: Uint8Array;
  statusCode?: number | null;
  headers?: HeadersEntry[];
}

export interface HeadersEntry {
  key?: string;
  value?: string;
}

export interface RawHttpRequest {
  body?: Uint8Array;
  pathParams?: PathParametersEntry[];
  queryParams?: QueryParametersEntry[];
  headers?: HeadersEntry[];
  method?: string;
  rawPath?: string;
  rawQuery?: string;
}

export interface PathParametersEntry {
  key?: string;
  value?: string;
}

export interface QueryParametersEntry {
  key?: string;
  value?: string;
}

/** Request for the Jwks endpoint. Takes no parameters. */
export interface JwksRequest {}

export interface DeviceCodeRequest {
  /** The ID of the application that asks for authorization. */
  clientId?: string;
  /**
   * scope is a space-delimited string that specifies the requested scope of the
   * access request.
   */
  scope?: string | null;
}

export interface DeviceCodeResponse {
  /** is the unique code for the device. When the user goes to the verification_uri in their browser-based device, this code will be bound to their session. */
  deviceCode?: string;
  /** contains the code that should be input at the verification_uri to authorize the device. */
  userCode?: string;
  /** contains the URL the user should visit to authorize the device. */
  verificationUri?: string;
  /** indicates the lifetime (in seconds) of the device_code and user_code. */
  expiresIn?: number;
  /** indicates the interval (in seconds) at which the app should poll the token URL to request a token. clients MUST use 5 as the default */
  interval?: number | null;
}

export interface DeviceAuthorizeRequest {
  /** The ID of the application that asks for authorization. */
  clientId?: string;
  /** scope is a space-delimited string that specifies the requested scope of the access request. */
  scope?: string | null;
  /**
   * OIDC nonce, bound to the device authorization and echoed back in the id_token at /token.
   * @maxLength 512
   */
  nonce?: string | null;
}

export interface DeviceAuthorizeResponse {
  /** the unique code for the device, used to poll the token endpoint. */
  deviceCode?: string;
  /** the code the user enters at the verification_uri to authorize the device. */
  userCode?: string;
  /** the URL the user should visit to authorize the device. */
  verificationUri?: string;
  /** the verification_uri with the user_code embedded as a query param, ready to encode into a QR code. */
  verificationUriComplete?: string;
  /** lifetime (in seconds) of the device_code and user_code. */
  expiresIn?: number;
  /** interval (in seconds) at which the client should poll the token endpoint. clients MUST use 5 as the default. */
  interval?: number | null;
}

export interface DeviceVerifyRequest {
  /** User code representing a currently authorizing device. */
  userCode?: string;
}

export interface DeviceVerifyResponse {}

export interface DeviceVerifyV2Request {
  /**
   * User code representing a currently authorizing device.
   * @minLength 8
   * @maxLength 8
   */
  userCode?: string;
}

export interface DeviceVerifyV2Response {}

export interface DeviceAuthorizationInfoRequest {
  /**
   * User code representing a currently authorizing device.
   * @minLength 8
   * @maxLength 8
   */
  userCode?: string;
}

export interface DeviceAuthorizationInfoResponse {
  /** The ID of the application the device is authorizing into. */
  clientId?: string;
  /** Canonical browser name of the initiating browser (e.g. "chrome"). */
  browser?: string | null;
  /** Canonical OS of the initiating browser (e.g. "windows"). */
  os?: string | null;
  /** Canonical device class of the initiating browser (e.g. "desktop"). */
  deviceClass?: string | null;
  /** IP address of the initiating browser. */
  browserIp?: string | null;
  /** Coarse geo-location (country, optional region) derived from the browser IP, e.g. "IL" or "US-NY". */
  geo?: string | null;
}

export interface InvalidateUserCodeRequest {
  /** user code to invalidate. Only the authorizing identity is able to invalidate it. */
  userCode?: string;
}

export interface InvalidateUserCodeResponse {}

export interface RevokeRefreshTokenRequest {
  /** The refresh token itself. Anyone with the token itself is able to revoke it. */
  token?: string;
  /** The client id that issued the refresh token. */
  clientId?: string | null;
}

export interface RevokeRefreshTokenResponse {}

export interface Empty {}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface UserDeletedEvent {
  /**
   * User ID from the deletion event.
   * @format GUID
   */
  userId?: string;
}

export interface UserBlockedEvent {
  /**
   * User ID from the deletion event.
   * @format GUID
   */
  userId?: string;
}

export interface UserInfoRequest {}

/** UserInfo endpoint response for OpenID Connect */
export interface UserInfoResponse {
  /**
   * Subject identifier — unique identifier for the user (OIDC standard claim).
   * @format GUID
   */
  sub?: string;
  /**
   * The user's given name.
   * @maxLength 20
   */
  givenName?: string | null;
  /**
   * The user's family name.
   * @maxLength 20
   */
  familyName?: string | null;
  /**
   * The user's email address.
   * @format EMAIL
   */
  email?: string | null;
  /** Indicates whether the user's email address has been verified. */
  emailVerified?: boolean | null;
  /**
   * The URL of the user's profile picture.
   * @format WEB_URL
   */
  picture?: string | null;
  /**
   * The user's preferred language, if available.
   * @format LANGUAGE
   */
  language?: string | null;
  /** The user's full name. */
  name?: string | null;
}

/**
 * Creates an access token.
 *
 *
 * The endpoint accepts raw HTTP requests. You must pass the request's body
 * parameters formatted as bytes in the raw HTTP request's `body` field,
 * following this template:
 * `{"grantType": "client_credentials", "client_id": "<APP_ID>", "client_secret": "<APP_SECRET_KEY>", "instance_id": "<INSTANCE_ID>"}`.
 *
 * When the call succeeds, Wix returns `{"statusCode": 200}` and the created access
 * token in the `body` field of the raw HTTP response.
 *
 * In case the call fails, Wix returns the relevant `4XX` error code in the raw
 * HTTP response's `statusCode` field and details
 * about the error in `body`. Error details follow the
 * [conventions of the Internet Engineering Task Force (IETF)](https://datatracker.ietf.org/doc/html/rfc6749#appendix-A.7).
 * @public
 * @fqn wix.identity.oauth2.v1.Oauth2Ng.Token
 */
export async function token(
  options?: TokenOptions
): Promise<
  NonNullablePaths<
    RawHttpResponse,
    `body` | `headers` | `headers.${number}.key` | `headers.${number}.value`,
    2
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    body: options?.body,
    pathParams: options?.pathParams,
    queryParams: options?.queryParams,
    headers: options?.headers,
    method: options?.method,
    rawPath: options?.rawPath,
    rawQuery: options?.rawQuery,
  });

  const reqOpts = ambassadorWixIdentityOauthV1RefreshToken.token(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          body: '$[0].body',
          pathParams: '$[0].pathParams',
          queryParams: '$[0].queryParams',
          headers: '$[0].headers',
          method: '$[0].method',
          rawPath: '$[0].rawPath',
          rawQuery: '$[0].rawQuery',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface TokenOptions {
  body?: Uint8Array;
  pathParams?: PathParametersEntry[];
  queryParams?: QueryParametersEntry[];
  headers?: HeadersEntry[];
  method?: string;
  rawPath?: string;
  rawQuery?: string;
}

/**
 * Returns the JWKS (JSON Web Key Set) with the RSA public key used to verify asymmetrically-signed
 * tokens (e.g. OIDC id_token). Required by external OIDC relying parties.
 * Serves a non-secret public key.
 * @public
 * @documentationMaturity preview
 * @fqn wix.identity.oauth2.v1.Oauth2Ng.Jwks
 */
export async function jwks(): Promise<
  NonNullablePaths<
    RawHttpResponse,
    `body` | `headers` | `headers.${number}.key` | `headers.${number}.value`,
    2
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({});

  const reqOpts = ambassadorWixIdentityOauthV1RefreshToken.jwks(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {},
        singleArgumentUnchanged: false,
      },
      []
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Token Introspection Endpoint.
 * @public
 * @fqn wix.identity.oauth2.v1.Oauth2Ng.TokenInfo
 */
export async function tokenInfo(
  options?: TokenInfoOptions
): Promise<
  NonNullablePaths<
    TokenInfoResponse,
    `active` | `subjectType` | `subjectId` | `clientId`,
    0
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    body: options?.body,
    pathParams: options?.pathParams,
    queryParams: options?.queryParams,
    headers: options?.headers,
    method: options?.method,
    rawPath: options?.rawPath,
    rawQuery: options?.rawQuery,
  });

  const reqOpts = ambassadorWixIdentityOauthV1RefreshToken.tokenInfo(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          body: '$[0].body',
          pathParams: '$[0].pathParams',
          queryParams: '$[0].queryParams',
          headers: '$[0].headers',
          method: '$[0].method',
          rawPath: '$[0].rawPath',
          rawQuery: '$[0].rawQuery',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface TokenInfoOptions {
  body?: Uint8Array;
  pathParams?: PathParametersEntry[];
  queryParams?: QueryParametersEntry[];
  headers?: HeadersEntry[];
  method?: string;
  rawPath?: string;
  rawQuery?: string;
}

/**
 * OpenID Connect UserInfo endpoint
 * Should be called with user access token obtained in OIDC flow as Authorization header
 * @public
 * @documentationMaturity preview
 * @returns UserInfo endpoint response for OpenID Connect
 * @fqn wix.identity.oauth2.v1.Oauth2Ng.UserInfo
 */
export async function userInfo(): Promise<
  NonNullablePaths<UserInfoResponse, `sub`, 0>
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({});

  const reqOpts = ambassadorWixIdentityOauthV1RefreshToken.userInfo(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {},
        singleArgumentUnchanged: false,
      },
      []
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
